import React, {useEffect, useState} from 'react';
import {Button, NavBar} from 'antd-mobile';
import {getBottomNavBarContainerStyle, getNavBarContainerStyle} from "../../../../utils/deviceUtils";
import {ArrowLeftOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {playScanFailedSound, playScanSound} from '../../../../utils/scanUtils';
import {showRepeatedScanError, parseExceptionMessageKey} from '../../../../utils/exceptionUtils';
import {PickupInfoPanel, PickupInfoPanelProps} from "../../../../components/PickupInfoPanel";
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {
    DSP_ARRIVAL_SCAN,
    DspArrivalScanResponse,
    DspArrivalScanVariables
} from 'pages/delivery/graphql/mutations/dspArrivalScan';
import {DspPath} from "../../../../enums/DeliveryNavigatePathEnum";
import {BarCodeScanner, useBarCodeScanner} from 'components/BarCodeScanner';
import {isTrackingNumber} from "../../../../utils/BarCodeUtil";
import {showErrorNoTitle} from "../../../../utils/exceptionUtils";
import {
    DspDeliveryTaskOrderListResponse,
    GET_DSP_DELIVERY_TASK_ORDER_LIST
} from "../../graphql/queries/dspDeliveryTaskOrderList";
import {DeliveryTaskOrderType} from 'pages/delivery/graphql/types/DeliveryTaskOrderType';
import {useLocationStore} from "../../../../store/locationStore";
import {t} from "../../../../i18n";
import {compareDeliveryTaskOrders, formatStopNumber} from "../../../../utils/stopNumberUtils";
import { checkSameAreaEnabled } from '../../../../utils/featureToggleUtils';
import {
    GET_DELIVERY_TASK_ORDER_BY_TRACKING_NUMBER,
    GetDeliveryTaskOrderByTrackingNumberResponse
} from "../../../delivery/graphql/queries/getDeliveryTaskOrderByTrackingNumber";
import useDspWarehouseStore from "../../store/dspWarehouseStore";


const PageBody: React.FC = () => {
    const navigate = useNavigate();
    const [lastScannedItem, setLastScannedItem] = useState<PickupInfoPanelProps | null>(null);
    const [orderTotal,setOrderTotal] = useState<number>(0);
    const [trackingNumbers,setTrackingNumbers] = useState<string[]>([]);
    const [arrivalScanData,setArrivalScanData] = useState<DeliveryTaskOrderType[]>([]);
    const { position } = useLocationStore();
    const {scanResult, setIsVisible} = useBarCodeScanner();
    const [handleDspArrivalScan] = useMutation<DspArrivalScanResponse,DspArrivalScanVariables>(DSP_ARRIVAL_SCAN);
    const selectedWarehouseId = useDspWarehouseStore((state) => state.selectedWarehouseId);
    const { data } = useQuery<DspDeliveryTaskOrderListResponse>(GET_DSP_DELIVERY_TASK_ORDER_LIST, {
        variables: { warehouseId: selectedWarehouseId ? parseInt(selectedWarehouseId) : undefined },
    });
    const dspDeliveryTasks = data?.getDeliveryTaskOrderListByDspId || [];
    const [getDeliveryTaskOrderByTrackingNumber, { data: trackingData, loading: trackingLoading }] = useLazyQuery<GetDeliveryTaskOrderByTrackingNumberResponse>(
        GET_DELIVERY_TASK_ORDER_BY_TRACKING_NUMBER
    );

    useEffect(() => {
        if (!dspDeliveryTasks.length) return;
        setArrivalScanData(dspDeliveryTasks);
        setOrderTotal(dspDeliveryTasks.length);
    }, [data]);

    useEffect(() => {
        setIsVisible(true);
        return () => {
            setIsVisible(false);
        }
    }, []);

    useEffect(() => {
        if (scanResult){
            handleScanSuccess(scanResult);
        }
    }, [scanResult]);

    // 处理扫描成功
    const handleScanSuccess = async (id: string) => {
        if (id === lastScannedItem?.id) {
            return;
        }

        if (!isTrackingNumber(id)){
            return;
        }

        try {
            const { data } = await handleDspArrivalScan({
              variables: {
                input: {
                  trackingNumber: id,
                  scanLocation: {
                    latitude: position?.latitude || 0.0,
                    longitude: position?.longitude || 0.0
                  }
                }
              }
            });
            const dspScanDataList = data?.handleDspArrivalScanEvent;
            if (dspScanDataList){
                await playScanSound();
                dspScanDataList
                    ?.map(dspScanData => {
                        setLastScannedItem({
                            id: dspScanData.trackingNumber || id,
                            status: 'success',
                            item: {
                                trackingNumber: dspScanData.trackingNumber,
                                stopNumber: dspScanData?.stopNumber ? formatStopNumber(dspScanData?.stopNumber,dspScanData?.subStopNumber) : '-',
                                routeName: dspScanData.routeName,
                            }});

                        if (dspScanData && !trackingNumbers.includes(dspScanData.trackingNumber)) {
                            setTrackingNumbers(prev => [...prev, dspScanData.trackingNumber]);
                        }
                    })
            }else {
                await playScanFailedSound();
                showErrorNoTitle('Wrong Package');
            }

        } catch (error) {
            console.error('handleScanSuccess error:', error);
            const messageKey = parseExceptionMessageKey(error);
            if (messageKey === "errors.business.alreadyScanned") {
                // 先通过trackingNumber调一个查询接口，给LastScannedItem赋值。
                getDeliveryTaskOrderByTrackingNumber({
                    variables: { trackingNumber: id , isTransferIn : false}
                }).then(({ data }) => {
                    if (data?.getDeliveryTaskOrderByTrackingNumber) {
                        const result = data.getDeliveryTaskOrderByTrackingNumber;
                        setLastScannedItem({
                            id,
                            status: 'success',
                            item: {
                                trackingNumber: result.trackingNumber,
                                stopNumber: result.stopNumber.toString(),
                                subStopNumber: result.subStopNumber,
                                routeName: result.routeName,
                            }
                        });
                    }
                    // 设置完 LastScannedItem 后再显示错误信息
                    showRepeatedScanError(error);
                });
            } else {
                await playScanFailedSound();
                showErrorNoTitle(error);
            }
            // 重置扫描状态，允许用户继续扫描
            setTimeout(() => {
                setIsVisible(true);
            }, 1000);
        }
    };

    const handleStartDelivery = async () => {
        // 直接调用后端接口获取灰度开关状态
        const useNewFeature = await checkSameAreaEnabled();
        
        if(useNewFeature){
            navigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_SUMMARY, {
                state: {
                    stats: {
                        orderTotal: orderTotal,
                        scannedTotal: trackingNumbers.length
                    },
                    notScannedOrders: arrivalScanData
                        .filter(item => item.trackingNumber && !trackingNumbers.includes(item.trackingNumber))
                        .sort((a, b) => {
                            return compareDeliveryTaskOrders(a, b);
                        })
                        .map(item => ({
                            id: item.trackingNumber || '',
                            route: item.routeName || '',
                            stopNumber: item.stopNumber ? formatStopNumber(item.stopNumber,item.subStopNumber) : '-'
                        }))
                }
            });
        }else{
            if (orderTotal === 0 || orderTotal === trackingNumbers.length) {
                navigate(DspPath.DELIVERY_DSP_HOME);
                return;
            }
            navigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_CONFIRM, {
                state: {
                    stats: {
                        orderTotal: orderTotal,
                        scannedTotal: trackingNumbers.length
                    },
                    notScannedOrders: arrivalScanData
                        .filter(item => item.trackingNumber && !trackingNumbers.includes(item.trackingNumber))
                        .sort((a, b) => {
                            return compareDeliveryTaskOrders(a, b);
                        })
                        .map(item => ({
                            id: item.trackingNumber || '',
                            route: item.routeName || '',
                            stopNumber: item.stopNumber ? formatStopNumber(item.stopNumber,item.subStopNumber) : '-'
                        }))
                }
            });
        }
    };


    return (
        <div className="flex flex-col h-screen bg-white">
            <div className="flex-1 relative">
                {lastScannedItem && <PickupInfoPanel id={lastScannedItem.id} status={lastScannedItem.status}
                                                     item={lastScannedItem.item}/>}
            </div>

            <div {...getBottomNavBarContainerStyle('sticky bottom-0 left-0 w-full p-4 bg-white z-20')}>
                <Button
                    onClick={handleStartDelivery}
                    color="primary"
                    className="w-full"
                >
                    {t('pages.dsp.home.receivingSorting.complete')}
                </Button>
            </div>
        </div>
    );
};

const ReceivingSortingScanPage: React.FC = () => {
    const navigate = useNavigate();
    
    return (
        <div className="flex flex-col h-screen bg-white overflow-hidden">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                    onBack={() => navigate(DspPath.DELIVERY_DSP_HOME)}
                    backIcon={<ArrowLeftOutlined/>}
                    right={
                        <Button
                            onClick={async () => {
                                // 根据灰度开关决定跳转到哪个详情页面
                                const useNewFeature = await checkSameAreaEnabled();
                                if (useNewFeature) {
                                    navigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_NEW_DETAILS);
                                } else {
                                    // 跳转到 main 分支对应的旧版页面
                                    navigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_DETAILS);
                                }
                            }}
                            color="primary"
                            className="mr-2"
                            size="mini"
                            fill={'none'}
                        >
                            {t('pages.details')}
                        </Button>
                    }
                >
                    {t('pages.dsp.home.sorting')}
                </NavBar>
            </div>
            <BarCodeScanner />
            <PageBody/>
        </div>
    );
};

export default ReceivingSortingScanPage;